import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  Inbox as InboxIcon,
  Mail,
  Trash2,
  Check,
  Eye,
  ArrowLeft,
  RefreshCw,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "hsr_admin_key";

const STATUS_STYLES = {
  new: "bg-clay-pink text-white",
  read: "bg-clay-lime text-clay-ink",
  handled: "bg-clay-surface text-clay-ink",
};

const brutalField =
  "border-[3px] border-clay-ink bg-white font-body text-clay-ink shadow-brutal-sm rounded-xl h-12 focus-visible:ring-clay-violet focus-visible:ring-2";

const Gate = ({ onUnlock }) => {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key) return;
    setLoading(true);
    try {
      await axios.post(`${API}/collab/verify`, {}, { headers: { "X-Admin-Key": key } });
      localStorage.setItem(KEY_STORE, key);
      onUnlock(key);
    } catch {
      toast.error("Wrong passcode. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="admin-gate-form"
        className="w-full max-w-md rounded-[2.25rem] border-[3px] border-clay-ink bg-white p-8 shadow-clay"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border-[3px] border-clay-ink bg-clay-violet shadow-brutal-sm">
          <Lock className="h-6 w-6 text-white" strokeWidth={2.25} />
        </span>
        <h1 className="mt-6 font-display text-3xl font-black tracking-tighter text-clay-ink">
          Inquiry Inbox
        </h1>
        <p className="mt-2 font-body text-sm font-medium text-clay-ink/70">
          Enter your private passcode to view brand collaboration requests.
        </p>
        <Input
          type="password"
          data-testid="admin-key-input"
          className={`${brutalField} mt-6`}
          placeholder="Passcode"
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-unlock-btn"
          className="mt-5 w-full rounded-full border-[3px] border-clay-ink bg-clay-violet px-6 py-3 font-display text-base font-bold text-white shadow-clay-btn transition-transform hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-60"
        >
          {loading ? "Checking..." : "Unlock inbox"}
        </button>
        <a
          href="/"
          data-testid="gate-back-home"
          className="mt-5 flex items-center justify-center gap-2 font-body text-sm font-bold text-clay-ink/60 hover:text-clay-violet"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back to media kit
        </a>
      </motion.form>
    </div>
  );
};

const StatCard = ({ label, value, bg }) => (
  <div className={`rounded-2xl border-[3px] border-clay-ink ${bg} p-5 shadow-brutal-sm`}>
    <p className="font-display text-4xl font-black tracking-tighter">{value}</p>
    <p className="mt-1 font-body text-xs font-bold uppercase tracking-wide opacity-75">{label}</p>
  </div>
);

const Dashboard = ({ apiKey, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const headers = { "X-Admin-Key": apiKey };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/collab`, { headers });
      setItems(res.data);
    } catch {
      toast.error("Session expired. Please log in again.");
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/collab/${id}`, { status }, { headers });
      setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/collab/${id}`, { headers });
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    total: items.length,
    new: items.filter((i) => i.status === "new").length,
    handled: items.filter((i) => i.status === "handled").length,
  };
  const fmt = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <a
            href="/"
            data-testid="dash-back-home"
            className="mb-2 flex items-center gap-2 font-body text-sm font-bold text-clay-ink/60 hover:text-clay-violet"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Media kit
          </a>
          <h1 className="font-display text-4xl font-black tracking-tighter text-clay-ink sm:text-5xl">
            Inquiry Inbox
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="dash-refresh-btn"
            className="flex items-center gap-2 rounded-full border-[3px] border-clay-ink bg-white px-4 py-2 font-display text-sm font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-y-[2px]"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2.5} /> Refresh
          </button>
          <button
            onClick={onLogout}
            data-testid="dash-logout-btn"
            className="rounded-full border-[3px] border-clay-ink bg-clay-ink px-4 py-2 font-display text-sm font-bold text-white shadow-brutal-sm transition-transform hover:translate-y-[2px]"
          >
            Lock
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4">
        <StatCard label="Total" value={counts.total} bg="bg-white" />
        <StatCard label="New" value={counts.new} bg="bg-clay-pink text-white" />
        <StatCard label="Handled" value={counts.handled} bg="bg-clay-lime" />
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {["all", "new", "read", "handled"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`rounded-full border-[3px] border-clay-ink px-4 py-1.5 font-body text-sm font-bold capitalize transition-transform hover:translate-y-[1px] ${
              filter === f ? "bg-clay-violet text-white shadow-brutal-sm" : "bg-white text-clay-ink"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4" data-testid="inquiry-list">
        {loading && (
          <p className="font-body text-sm font-medium text-clay-ink/60">Loading inquiries...</p>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-[2rem] border-[3px] border-dashed border-clay-ink/40 bg-white p-12 text-center">
            <InboxIcon className="mx-auto h-10 w-10 text-clay-ink/40" strokeWidth={2} />
            <p className="mt-3 font-body font-bold text-clay-ink/60">No inquiries here yet.</p>
          </div>
        )}
        <AnimatePresence>
          {filtered.map((it) => (
            <motion.div
              key={it.id}
              layout
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              data-testid={`inquiry-${it.id}`}
              className="rounded-[1.75rem] border-[3px] border-clay-ink bg-white p-6 shadow-clay"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-xl font-black tracking-tight text-clay-ink">
                      {it.name}
                    </h3>
                    <span
                      className={`rounded-full border-2 border-clay-ink px-2.5 py-0.5 font-body text-xs font-bold uppercase ${STATUS_STYLES[it.status]}`}
                    >
                      {it.status}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-sm font-medium text-clay-ink/70">
                    <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 hover:text-clay-violet">
                      <Mail className="h-4 w-4" strokeWidth={2.25} /> {it.email}
                    </a>
                    {it.brand && (
                      <span className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4" strokeWidth={2.25} /> {it.brand}
                      </span>
                    )}
                  </div>
                </div>
                <span className="rounded-full border-2 border-clay-ink bg-clay-surface px-3 py-1 font-body text-xs font-bold text-clay-ink">
                  {it.collab_type}
                </span>
              </div>

              <p className="mt-4 rounded-xl border-2 border-clay-ink/15 bg-clay-bg p-4 font-body text-sm leading-relaxed text-clay-ink/85">
                {it.message}
              </p>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <span className="font-body text-xs font-bold uppercase tracking-wide text-clay-ink/50">
                  {fmt(it.created_at)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStatus(it.id, "read")}
                    data-testid={`mark-read-${it.id}`}
                    className="flex items-center gap-1.5 rounded-full border-[3px] border-clay-ink bg-clay-lime px-3 py-1.5 font-body text-xs font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-y-[1px]"
                  >
                    <Eye className="h-3.5 w-3.5" strokeWidth={2.5} /> Read
                  </button>
                  <button
                    onClick={() => setStatus(it.id, "handled")}
                    data-testid={`mark-handled-${it.id}`}
                    className="flex items-center gap-1.5 rounded-full border-[3px] border-clay-ink bg-clay-violet px-3 py-1.5 font-body text-xs font-bold text-white shadow-brutal-sm transition-transform hover:translate-y-[1px]"
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> Handled
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    data-testid={`delete-${it.id}`}
                    className="flex items-center justify-center rounded-full border-[3px] border-clay-ink bg-white px-3 py-1.5 text-clay-ink shadow-brutal-sm transition-transform hover:translate-y-[1px]"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default function Inbox() {
  const [apiKey, setApiKey] = useState(localStorage.getItem(KEY_STORE) || "");

  const logout = () => {
    localStorage.removeItem(KEY_STORE);
    setApiKey("");
  };

  return (
    <div className="min-h-screen bg-clay-bg font-body text-clay-ink">
      {apiKey ? (
        <Dashboard apiKey={apiKey} onLogout={logout} />
      ) : (
        <Gate onUnlock={setApiKey} />
      )}
      <Toaster position="top-center" richColors />
    </div>
  );
}
